import {
  assistanceRowTypes,
  assistanceUniteNombresHeure,
  PrejudiceFormCalendrierAssistance,
  PrejudiceFormCalendrierAssistanceRow,
  PrejudiceType,
} from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import i18next from 'i18next';
import {
  getDefaultMinAndMaxDate,
  validationSchemaNumeroPieces,
  validationSchemaPrejudiceBase,
} from 'src/constants/prejudice/validation';
import { dateString } from 'src/helpers/yup';
import * as yup from 'yup';

export const validationSchemaCalendrierAssistanceRow = ({
  victime,
  procedure,
  prejudiceType,
}: {
  victime: Victime;
  procedure: Procedure;
  prejudiceType: PrejudiceType;
}): yup.ObjectSchema<PrejudiceFormCalendrierAssistanceRow> => {
  const { minDate, maxDate } = getDefaultMinAndMaxDate({
    victime,
    procedure,
    prejudiceType,
  });
  return yup.object({
    numerosPieces: validationSchemaNumeroPieces,
    joursPeriode: yup.number().defined().min(0),
    joursConges: yup.number().defined().min(0),
    joursRetenus: yup.number().defined().min(0),
    quantite: yup.number().defined().min(0),
    deductionFiscale: yup.number().defined().min(0),
    dateDebut: dateString()
      .nullable()
      .required()
      .minDate(undefined, minDate.message, minDate.date)
      .maxDate(undefined, maxDate.message, maxDate.date, { strict: true }),
    dateFin: dateString()
      .nullable()
      .required()
      .minDate(
        'dateDebut',
        i18next.t('validation.prejudices.dates.minDateDebut') || '',
      )
      .maxDate(undefined, maxDate.message, maxDate.date, { strict: true }),
    joursHospitalisationADeduire: yup.number().required().min(0),
    deduireJoursHospitalisation: yup.boolean().required(),
    totalJours: yup.number().required().min(0),
    typeAssistance: yup.string().optional(),
    forfaitHoraire: yup.number().required().min(0),
    nombreHeureParUniteChoisie: yup.number().required().min(0),
    uniteNombreHeure: yup
      .string()
      .required()
      .oneOf(assistanceUniteNombresHeure),
    dateJustificatif: dateString()
      .nullable()
      .defined()
      .when('montantsDejaRevalorises', {
        is: false,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema,
      }),
    montantsDejaRevalorises: yup.boolean().optional().defined(),
    montantTotal: yup.number().required().min(0),
    priseEnChargeTiersPayeurs: yup
      .array()
      .defined()
      .of(
        yup.object({
          tiersPayeur: yup.string().oneOf(procedure.tiersPayeurs).required(),
          montant: yup.number().required(),
        }),
      ),
    autresDeductions: yup.number().required().min(0),
    resteACharge: yup.number().defined().min(0),

    annualiseMontantTotal: yup.number().optional().min(0),
    commentaires: yup.string(),
    notes: yup.string().optional().defined(),
    isDFTPRow: yup.boolean().optional(),
    type: yup.string().oneOf(assistanceRowTypes).required(),
  });
};

export const validationSchemaCalendrierAssistance = ({
  victime,
  procedure,
  prejudiceType,
}: {
  victime: Victime;
  procedure: Procedure;
  prejudiceType: PrejudiceType;
}): yup.ObjectSchema<PrejudiceFormCalendrierAssistance> =>
  validationSchemaPrejudiceBase.shape({
    rows: yup.array().defined().of(
      validationSchemaCalendrierAssistanceRow({
        procedure,
        victime,
        prejudiceType,
      }),
    ),
    forfaitHoraire: yup.number().nullable().defined(),
    isDFTPRowsAdded: yup.boolean().optional(),
    revalorisationCoefficientsType: yup
      .string()
      .oneOf(['annuel', 'smic'])
      .required(),
  });
